import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { HelpCircle, MapPin, Truck } from "lucide-react";
import WorkshopPicker from "@/components/WorkshopPicker";

const CATEGORIES = ["iPhone", "iPad", "MacBook", "iMac"];

// Real-world symptom labels per device. These describe PROBLEMS — not
// solutions — and are stored on the order as free-form strings (the backend
// already accepts plain strings as symptoms).
const SYMPTOMS = {
  iPhone: [
    "Skjermen er knust eller svart",
    "Batteriet lades ikke eller tømmes fort",
    "Ladeport fungerer ikke",
    "Vannskade",
    "Høyttaler eller mikrofon fungerer ikke",
    "Annet",
  ],
  iPad: [
    "Skjermen er knust eller svart",
    "Batteriet lades ikke eller tømmes fort",
    "Lydproblemer",
    "Ikke respons på touch",
    "Annet",
  ],
  MacBook: [
    "Slår seg ikke på",
    "Batteriet holder ikke lenger",
    "Tastatur eller trackpad fungerer ikke",
    "Skjermen flimrer eller er svart",
    "Overoppheting",
    "Annet",
  ],
  iMac: [
    "Slår seg ikke på",
    "Skjerm-problemer",
    "Lagringsplass full eller disk-feil",
    "Systemet er tregt",
    "Annet",
  ],
};

const DESCRIPTION_LIMIT = 200;

export default function Diagnostic() {
  const nav = useNavigate();
  const { user } = useAuth();

  const [category, setCategory] = useState("iPhone");
  const [model, setModel] = useState("");
  const [selectedSymptoms, setSelectedSymptoms] = useState([]);
  const [description, setDescription] = useState("");
  const [diagnoseFee, setDiagnoseFee] = useState(null);

  const [fixers, setFixers] = useState([]);
  const [fixerId, setFixerId] = useState("");
  const [delivery, setDelivery] = useState("pickup");
  const [busy, setBusy] = useState(false);

  // Reset symptoms when category changes
  useEffect(() => {
    setSelectedSymptoms([]);
  }, [category]);

  // Diagnose fee at the selected workshop
  useEffect(() => {
    if (!fixerId) { setDiagnoseFee(null); return; }
    let cancelled = false;
    (async () => {
      const svcs = await api.get("/services").then((r) => r.data);
      const diag = svcs.find((s) => s.repair_type === "diagnose");
      if (!diag) return;
      const pricing = await api
        .get("/fixer-pricing", { params: { fixer_id: fixerId, service_id: diag.id } })
        .then((r) => r.data);
      if (!cancelled) setDiagnoseFee(pricing[0]?.labor_price ?? 499);
    })();
    return () => { cancelled = true; };
  }, [fixerId]);

  const toggleSymptom = (label) => {
    setSelectedSymptoms((prev) =>
      prev.includes(label) ? prev.filter((s) => s !== label) : [...prev, label],
    );
  };

  const canSubmit = useMemo(
    () => Boolean(category && fixerId),
    [category, fixerId],
  );

  const submit = async () => {
    if (!user) { nav("/logg-inn"); return; }
    if (user.role !== "customer") {
      toast.error("Bare kunder kan bestille diagnose.");
      return;
    }
    if (!canSubmit) return;
    setBusy(true);
    try {
      const { data: draft } = await api.post("/checkout/draft/diagnostic", {
        device_category: category,
        device_model: model || null,
        symptoms: selectedSymptoms,
        description: description || null,
        fixer_id: fixerId,
        delivery_method: delivery,
      });
      nav(`/kassen?draft=${draft.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="tk-container py-16 max-w-3xl" data-testid="diagnostic-page">
      <p className="tk-overline mb-3">Diagnose</p>
      <h1 className="text-3xl sm:text-4xl font-semibold mb-3 flex items-center gap-3">
        <HelpCircle className="text-[#4A7C59]" size={32} />
        Usikker på hva som er galt?
      </h1>
      <p className="text-sm text-slate-500 mb-10 max-w-xl">
        Vi diagnostiserer enheten din, sender deg en rapport med anbefalt reparasjon
        og fast pris. Du godkjenner eller avslår før vi begynner.
      </p>

      {/* Step 1: device + optional model */}
      <section className="mb-10">
        <h2 className="text-lg font-semibold mb-3">1. Velg enhet</h2>
        <div className="flex flex-wrap gap-2 mb-4" data-testid="diag-category-picker">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`px-4 py-2 rounded-md text-sm border ${category === c ? "bg-[#0B1C30] text-white border-[#0B1C30]" : "border-[#E2E8F0] bg-white text-slate-700 hover:bg-slate-50"}`}
              data-testid={`diag-cat-${c}`}
            >
              {c}
            </button>
          ))}
        </div>
        <label className="text-sm font-medium text-slate-700">
          Hvilken modell? <span className="text-slate-400 font-normal">(valgfritt)</span>
        </label>
        <input
          className="tk-input mt-1 max-w-md"
          placeholder="f.eks. iPhone 14, MacBook Pro 2021"
          value={model}
          onChange={(e) => setModel(e.target.value)}
          data-testid="diag-model-input"
        />
      </section>

      {/* Step 2: symptoms */}
      <section className="mb-10">
        <h2 className="text-lg font-semibold mb-1">
          2. Velg symptomer <span className="text-sm text-slate-400 font-normal">(valgfritt)</span>
        </h2>
        <p className="text-xs text-slate-500 mb-4">
          Velg én eller flere. Dette hjelper verkstedet, men er ikke bindende.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
          {(SYMPTOMS[category] || []).map((label) => {
            const checked = selectedSymptoms.includes(label);
            return (
              <button
                type="button"
                key={label}
                onClick={() => toggleSymptom(label)}
                className={`text-left px-4 py-3 rounded-md border flex items-start gap-3 ${checked ? "border-[#0B1C30] bg-slate-50" : "border-[#E2E8F0] bg-white hover:bg-slate-50"}`}
                data-testid={`diag-symptom-${label.slice(0, 24)}`}
              >
                <span
                  className={`mt-0.5 w-4 h-4 rounded border ${checked ? "bg-[#0B1C30] border-[#0B1C30]" : "border-[#E2E8F0]"}`}
                />
                <span className="font-medium text-sm">{label}</span>
              </button>
            );
          })}
        </div>

        <label className="text-sm font-medium text-slate-700">
          Beskriv problemet med egne ord{" "}
          <span className="text-slate-400 font-normal">(valgfritt)</span>
        </label>
        <textarea
          className="tk-input mt-1 min-h-[80px]"
          maxLength={DESCRIPTION_LIMIT}
          value={description}
          onChange={(e) => setDescription(e.target.value.slice(0, DESCRIPTION_LIMIT))}
          placeholder="f.eks. Skjermen blir helt svart etter 5 sekunder, men telefonen vibrerer fortsatt."
          data-testid="diag-description-input"
        />
        <p className="text-xs text-slate-400 mt-1 text-right">
          {description.length}/{DESCRIPTION_LIMIT}
        </p>
      </section>

      {/* Step 3: fixer */}
      <section className="mb-10">
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <MapPin size={18} /> 3. Velg verksted
        </h2>
        <WorkshopPicker
          extraParams={{ category }}
          selectedId={fixerId}
          onSelect={setFixerId}
          onLoad={setFixers}
          emptyMessage="Ingen verksteder tilgjengelig akkurat nå."
          renderItem={(f, isSelected, onSelect) => (
            <button
              type="button"
              key={f.id}
              onClick={onSelect}
              className={`w-full text-left px-4 py-3 rounded-md border ${isSelected ? "border-[#0B1C30] bg-slate-50" : "border-[#E2E8F0] bg-white hover:bg-slate-50"}`}
              data-testid={`diag-fixer-${f.id}`}
            >
              <p className="font-medium text-sm">{f.display_name}</p>
              <p className="text-xs text-slate-500">
                {f.address}, {f.postal_code}
                {typeof f.distance_km === "number" && ` · ${f.distance_km} km`}
              </p>
            </button>
          )}
        />
      </section>

      {/* Step 4: delivery */}
      <section className="mb-10">
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Truck size={18} /> 4. Levering
        </h2>
        <div className="flex gap-2">
          {[
            { k: "pickup", l: "Lever inn selv" },
            { k: "shipping", l: "Send med Bring" },
          ].map((d) => (
            <button
              type="button"
              key={d.k}
              onClick={() => setDelivery(d.k)}
              className={`px-4 py-2 rounded-md text-sm border ${delivery === d.k ? "bg-[#0B1C30] text-white border-[#0B1C30]" : "border-[#E2E8F0] bg-white text-slate-700"}`}
              data-testid={`diag-delivery-${d.k}`}
            >
              {d.l}
            </button>
          ))}
        </div>
      </section>

      <div className="tk-card flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500">Diagnose-avgift</p>
          <p className="text-3xl font-semibold" data-testid="diag-fee">
            {diagnoseFee != null ? `kr ${diagnoseFee.toFixed(0)}` : "—"}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Du betaler kun denne avgiften nå. Reparasjon avtales etter diagnose.
          </p>
        </div>
        <button
          onClick={submit}
          disabled={busy || !canSubmit}
          className="tk-btn-primary !py-3 !px-6"
          data-testid="diag-submit-btn"
        >
          {busy ? "Sender …" : "Bestill diagnose"}
        </button>
      </div>
    </div>
  );
}
